'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'

export default function Post18Content() {
  return (
    <BlogPostLayout
      title="Microplastics in Seafood: Which Fish Are Safest to Eat?"
      author="MicroPlastics Team"
      date="October 20, 2025"
      readTime="9 min read"
      heroImage="/blog/app-home.png"
      heroImageAlt="MicroPlastics app analyzing seafood products"
    >
      <BlogTLDR>
        Microplastics have been detected in <strong>every type of seafood
        studied</strong>. The cleanest options are{' '}
        <strong>fin fish where you do not eat the gut</strong> (wild salmon,
        cod, sardines from less-polluted regions). The worst are{' '}
        <strong>filter-feeding shellfish</strong> (mussels, oysters, clams)
        and small whole fish where you eat the digestive tract. Wild beats
        farmed on average; smaller fish lower on the food chain accumulate
        less than top predators.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Microplastics detected in essentially every type of seafood studied, particles concentrate in the gut.</>,
          <>Highest: filter-feeding shellfish (mussels, oysters, clams) and small whole fish where you eat the digestive tract.</>,
          <>Lowest: gut-removed fin fish from cleaner waters (wild salmon, Pacific cod, sardines).</>,
          <>Wild fish typically lower than farmed (farmed gut contents include feed-derived plastics).</>,
          <>Trim and clean carefully: removing skin, gut, and dark muscle reduces exposure measurably.</>,
          <>2026 update: seafood is high per gram but is <em>not</em> the top dietary contributor, fruit, vegetables and grains supply ~99.5% of daily particles.</>,
          <>A 2026 synthesis of 1,107 studies found measurable effects in <strong>95.2%</strong> of seafood specimens tested, though those are effects on the animals, not proven human outcomes.</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Introduction: The Hidden Cost of Seafood
      </h2>
      <p>
        Seafood has long been celebrated as one of the healthiest protein sources available. Rich in
        omega-3 fatty acids, lean protein, vitamins, and minerals, fish and shellfish are staples of
        heart-healthy and brain-boosting diets worldwide. But in recent years, a growing body of
        scientific research has revealed an uncomfortable reality: the oceans that produce our seafood
        are also repositories for millions of tons of plastic waste, and that plastic is making its way
        onto our plates.
      </p>
      <p>
        An estimated eight million metric tons of plastic enter the world&apos;s oceans every year.
        Over time, sunlight, wave action, and salt water break this plastic down into microplastics,
        particles smaller than five millimeters, and even smaller nanoplastics that measure less than
        one micrometer. These particles are now so pervasive in marine environments that researchers
        have detected them in every ocean basin, from Arctic ice to deep-sea sediments. Marine
        organisms ingest these particles at every level of the food chain, from microscopic plankton
        to the largest whales.
      </p>
      <p>
        For seafood lovers, this raises urgent questions. Which fish and shellfish contain the most
        microplastics? Are some species safer than others? Does it matter whether your fish is wild-caught
        or farmed? This guide examines the latest research to help you make informed choices about the
        seafood you eat while still enjoying its considerable nutritional benefits. For a broader look at
        how microplastics contaminate our diets, see our comprehensive guide on{' '}
        <Link href="/microplastics-in-food" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in food
        </Link>.
      </p>

      {/* How Microplastics Enter the Marine Food Chain */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026 Update: Seafood Is Not Actually Your Biggest Dietary Source
      </h2>
      <p>
        Two 2026 papers pull in useful opposite directions here, and holding both is the
        honest position.
      </p>
      <p>
        The first sharpens the concern. A global synthesis by Wootton and colleagues in{' '}
        <em>Fish and Fisheries</em> screened 1,107 studies and found measurable effects in{' '}
        <strong>95.2% of all seafood specimens tested</strong> -- spanning behaviour, growth
        and development, immune and reproductive function, biomarker expression, and
        mortality. Note carefully what that figure describes: effects on{' '}
        <em>the animals</em>, documented largely in experimental exposures. It is a
        statement about marine biology, not a measured human health outcome.
      </p>
      <p>
        The second reframes the dietary picture. A University of Amsterdam study in the{' '}
        <em>Journal of Hazardous Materials</em> compared 13 food categories and found that{' '}
        <strong>fruit, vegetables and grains together account for about 99.5% of daily
        microplastic particle intake</strong> -- ahead of seafood, simply because they are
        eaten far more often and in far greater mass.
      </p>
      <p>
        So seafood remains among the highest-concentration foods per gram, especially{' '}
        <strong>shellfish eaten whole</strong>, where you consume the digestive tract along
        with everything in it. But it is not where most of your particles come from unless
        you eat it daily. The practical guidance is unchanged and modest: favour finfish
        fillets over whole shellfish, and do not let this displace the well-established
        cardiovascular benefits of eating fish.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Microplastics Enter the Marine Food Chain
      </h2>
      <p>
        Understanding how microplastics move through the ocean ecosystem is essential to understanding
        why certain seafood species are more contaminated than others. The journey begins with plastic
        debris entering waterways through rivers, stormwater runoff, wastewater treatment plants, and
        direct dumping. Once in the ocean, larger plastic items gradually fragment into smaller and
        smaller pieces through photodegradation and mechanical weathering.
      </p>
      <p>
        At the base of the marine food chain, phytoplankton and zooplankton encounter these
        microplastic particles suspended in the water column. Studies have shown that zooplankton
        readily ingest microplastics, mistaking them for food due to their similar size. Small fish
        and filter-feeding organisms like mussels and oysters then consume both the contaminated
        plankton and free-floating microplastics directly from the water. As larger predators eat
        smaller organisms, microplastics accumulate up the food chain in a process known as
        trophic transfer.
      </p>
      <p>
        Filter-feeding organisms are particularly vulnerable because their feeding mechanism involves
        processing enormous volumes of water to extract nutrients. A single mussel can filter up to
        twenty-five liters of water per day, concentrating any microplastics present in that water
        within its tissues. This is why shellfish consistently rank among the most microplastic-laden
        seafood categories in scientific studies.
      </p>
      <p>
        Beyond ingestion, microplastics also adsorb persistent organic pollutants, heavy metals, and
        other toxic chemicals from seawater onto their surfaces. When a marine organism ingests a
        microplastic particle, it may also be ingesting a concentrated dose of these adsorbed
        contaminants, amplifying the potential health impact beyond the plastic itself. To learn more
        about these health implications, read our article on{' '}
        <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-medium">
          microplastics and health effects
        </Link>.
      </p>

      {/* Which Seafood Has the Most Microplastics */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Which Seafood Has the Most Microplastics?
      </h2>
      <p>
        Not all seafood carries the same microplastic burden. The key factor is whether you eat the
        entire organism, including its digestive system, or only the muscle tissue (fillet). Organisms
        consumed whole tend to have dramatically higher microplastic counts because the gastrointestinal
        tract is where ingested plastics accumulate.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Mussels and Oysters: The Highest Risk</h3>
      <p>
        Bivalve mollusks like mussels, oysters, clams, and scallops consistently rank as the most
        microplastic-contaminated seafood. As filter feeders, they strain massive volumes of seawater
        through their bodies to extract food particles. Any microplastics suspended in that water become
        trapped in their tissues. Because we eat these organisms whole, including their digestive tract,
        gills, and soft tissues, we consume every microplastic particle they have accumulated.
      </p>
      <p>
        Research published in <em>Environmental Pollution</em> found that mussels can contain anywhere
        from 0.2 to 7.2 microplastic particles per gram of tissue, depending on their growing
        environment. A typical serving of mussels weighing around two hundred grams could therefore
        contain up to 1,440 microplastic particles. Oysters show similar contamination levels, with
        studies finding an average of 0.5 to 5 particles per gram. Farmed mussels and oysters grown
        in coastal waters near urban areas or industrial zones tend to have the highest concentrations.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Shrimp and Prawns: A Close Second</h3>
      <p>
        Shrimp and prawns are among the most popular seafood items globally, and unfortunately, they
        also carry significant microplastic loads. While larger shrimp are often deveined and peeled
        before eating (which removes the digestive tract and reduces exposure somewhat), many
        preparations involve eating smaller shrimp whole. Even when deveined, microplastics can
        migrate from the gut into surrounding muscle tissue over time.
      </p>
      <p>
        A 2020 study in <em>Environmental Science &amp; Technology</em> found microplastics in 75
        percent of shrimp samples tested from markets across multiple countries. The average
        concentration was approximately one to two particles per gram of shrimp tissue. Shrimp from
        heavily polluted coastal waters and intensive aquaculture farms showed the highest levels.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Small Fish Eaten Whole</h3>
      <p>
        Anchovies, sardines, and other small fish that are often consumed whole, including their
        intestines, represent another high-exposure category. A study in <em>Marine Pollution
        Bulletin</em> examined sardines and anchovies from the Mediterranean and found microplastics
        in the gastrointestinal tracts of over 50 percent of samples. When these fish are eaten
        whole, as they commonly are in many cuisines, all those accumulated particles are consumed.
        However, when sardines are filleted and the gut cavity is removed, the microplastic exposure
        drops significantly.
      </p>

      {/* Fish That Are Relatively Safer */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Fish That Are Relatively Safer
      </h2>
      <p>
        The encouraging news is that many popular fish species, when consumed as fillets, have
        considerably lower microplastic contamination. The distinction that matters is that filleting
        removes the gastrointestinal tract, gills, and other organs where microplastics primarily
        accumulate. The muscle tissue of most fish contains far fewer particles.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Filleted Fish: Lower Risk</h3>
      <p>
        When you eat a salmon fillet, a piece of cod, or a tuna steak, you are eating only the
        muscle tissue. Multiple studies have found that the microplastic concentration in fish muscle
        is significantly lower than in the viscera. Research published in <em>Science of the Total
        Environment</em> showed that while the gastrointestinal tract of fish may contain dozens
        of microplastic particles, the muscle tissue typically contains fewer than one particle per
        gram.
      </p>
      <p>
        Species that tend to be lower in microplastics when consumed as fillets include:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Salmon</strong> (especially wild-caught Alaskan salmon), which lives in relatively
          clean, cold waters and is almost always consumed as fillets.
        </li>
        <li>
          <strong>Cod and haddock</strong>, deep-water species that are typically filleted, removing
          the organs where microplastics concentrate.
        </li>
        <li>
          <strong>Pollock</strong>, commonly used in fish sticks and fish sandwiches, always consumed
          as processed fillets with all organs removed.
        </li>
        <li>
          <strong>Trout</strong>, particularly freshwater trout from clean mountain streams and lakes,
          tends to have lower microplastic exposure than coastal species.
        </li>
        <li>
          <strong>Mahi-mahi and halibut</strong>, open-ocean species typically sold as thick fillets
          or steaks with all viscera removed during processing.
        </li>
      </ol>
      <p>
        &quot;Safer&quot; is a relative term. Even filleted fish are not
        microplastic-free. Nanoplastics, which are small enough to penetrate cell membranes, can
        migrate from the gut into muscle tissue and other organs. However, the concentrations in
        muscle tissue remain substantially lower than in whole organisms.
      </p>

      {/* Wild vs Farmed Fish */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Wild-Caught vs Farmed Fish: Which Is Better?
      </h2>
      <p>
        The wild-versus-farmed debate is more nuanced for microplastics than many people
        assume. Neither category is universally better, and the answer often depends on the
        specific species, farming practices, and geographic location.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">The Case for Wild-Caught</h3>
      <p>
        Wild fish living in open ocean environments generally encounter lower concentrations of
        microplastics compared to those in coastal or enclosed waters. Deep-sea species and those
        from remote, less-polluted regions like Alaska, the southern Pacific, and the open Atlantic
        tend to have among the lowest microplastic levels. Wild fish also eat a natural diet, which
        means their microplastic exposure comes solely from their environment and prey, not from
        manufactured feed.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">The Case for Farmed Fish</h3>
      <p>
        Farmed fish present a mixed picture. On one hand, aquaculture operations in enclosed or
        controlled environments could theoretically produce fish with lower microplastic exposure
        if the water quality is carefully managed. Some land-based recirculating aquaculture systems
        (RAS) can filter water to remove particulates, potentially reducing microplastic intake.
      </p>
      <p>
        On the other hand, many ocean-based fish farms are located in coastal waters that tend to
        have higher microplastic concentrations due to proximity to urban runoff, industrial
        discharge, and shipping lanes. Additionally, commercial fish feed pellets have been found
        to contain microplastics. A 2021 study in <em>Aquaculture</em> detected microplastic
        particles in multiple brands of commercial fish feed, meaning farmed fish may ingest
        microplastics from their feed as well as from the surrounding water.
      </p>
      <p>
        The bottom line: the location and practices of the farm matter more than whether the fish is
        wild or farmed. When possible, look for fish from well-managed fisheries in clean waters,
        whether wild or farmed. Certifications like the Marine Stewardship Council (MSC) for wild
        fish and the Aquaculture Stewardship Council (ASC) for farmed fish can help guide your choices.
      </p>

      {/* Geographic Differences */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Geographic Differences in Seafood Contamination
      </h2>
      <p>
        Where your seafood comes from matters enormously. Microplastic concentrations in the ocean
        vary dramatically by region, and this directly affects the contamination levels in marine
        organisms harvested from those waters.
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Highest contamination zones:</strong> The Mediterranean Sea, the South China Sea,
          and coastal waters near major Asian manufacturing hubs consistently show the highest
          microplastic concentrations. Seafood from these regions tends to carry the heaviest
          microplastic burdens. The Mediterranean is essentially a semi-enclosed basin surrounded by
          densely populated coastlines, trapping plastic pollution with limited exchange with the
          open Atlantic.
        </li>
        <li>
          <strong>Moderate contamination zones:</strong> The North Atlantic, North Pacific, and
          coastal waters near major cities worldwide fall into a middle range. These areas are
          affected by ocean gyres that concentrate floating plastic debris, as well as by local
          pollution from coastal populations.
        </li>
        <li>
          <strong>Lower contamination zones:</strong> Remote regions like the waters off Alaska,
          Norway, Iceland, New Zealand, and parts of the southern Pacific and southern Atlantic tend
          to have lower microplastic concentrations. Seafood from these areas, while not
          microplastic-free, generally has lower contamination levels.
        </li>
      </ol>
      <p>
        When shopping for seafood, pay attention to country of origin labels. Seafood from northern,
        remote, or well-regulated fisheries tends to be a better choice from a microplastic
        perspective. Fish labeled as product of China, Vietnam, Thailand, or Indonesia may carry
        higher microplastic loads due to the generally higher contamination levels in surrounding
        waters, though this is a generalization and individual results can vary.
      </p>

      {/* Shellfish vs Finfish */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Shellfish vs Finfish: A Direct Comparison
      </h2>
      <p>
        When comparing broad categories of seafood, the distinction between shellfish and finfish
        is one of the most important factors in microplastic exposure.
      </p>
      <p>
        <strong>Shellfish</strong> (mussels, oysters, clams, shrimp, crab, lobster) are generally
        higher in microplastics for several reasons. Bivalves are filter feeders that process huge
        volumes of water. Shrimp and crab live on or near the ocean floor where microplastics tend
        to settle and accumulate in sediment. Many shellfish are eaten whole or with minimal
        processing, meaning the digestive tract and its accumulated microplastics are consumed.
      </p>
      <p>
        <strong>Finfish</strong> (salmon, cod, tuna, halibut, and similar species) are generally
        lower in microplastics when consumed as fillets. Their feeding mechanisms do not involve
        filtering water in the same way as bivalves. When properly filleted, the organs that
        accumulate the most microplastics are removed. The muscle tissue that we eat contains
        significantly fewer particles per gram compared to whole shellfish.
      </p>
      <p>
        This does not mean you need to eliminate shellfish entirely. Rather, it means being
        strategic about how often you eat them and where they are sourced. Treating shellfish as
        an occasional delicacy rather than a daily staple, and choosing specimens from cleaner
        waters, can meaningfully reduce your overall microplastic intake from seafood.
      </p>

      {/* Cooking Methods */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Can Cooking Methods Help Reduce Microplastics?
      </h2>
      <p>
        Once microplastics are embedded in seafood tissue, no cooking method can fully eliminate
        them. However, certain preparation and cooking techniques may help reduce your exposure.
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Remove the gut and organs.</strong> The most effective step is to properly
          clean and gut your fish. Removing the head, digestive tract, gills, and internal organs
          eliminates the areas where microplastics are most concentrated. Even with small fish like
          sardines, taking the time to fillet and gut them rather than eating them whole can
          substantially reduce microplastic intake.
        </li>
        <li>
          <strong>Rinse thoroughly.</strong> Rinsing fish fillets and shellfish under clean, filtered
          water before cooking can wash away surface microplastic particles. While this will not
          remove particles embedded in tissue, it can reduce surface contamination.
        </li>
        <li>
          <strong>Boiling and discarding the water.</strong> When cooking shellfish like mussels or
          clams, boiling them and discarding the cooking liquid may remove some microplastics that
          leach out during cooking. However, research on this method is still limited.
        </li>
        <li>
          <strong>Avoid adding more plastic during cooking.</strong> Use stainless steel, cast iron,
          or ceramic cookware rather than non-stick pans with plastic coatings. Do not wrap fish in
          plastic wrap before cooking. Use parchment paper or banana leaves instead of plastic bags
          for steaming or sous vide preparations.
        </li>
        <li>
          <strong>Devein shrimp properly.</strong> The dark vein running along the back of a shrimp
          is actually its digestive tract. Removing this before cooking eliminates the area with the
          highest concentration of ingested microplastics. Always devein shrimp regardless of size.
        </li>
      </ol>

      {/* Sustainable Choices */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Sustainable Seafood Choices That Also Minimize Microplastics
      </h2>
      <p>
        There is significant overlap between sustainable seafood practices and lower microplastic
        contamination. Fisheries and aquaculture operations that prioritize environmental
        stewardship tend to operate in cleaner waters and use practices that result in lower
        contamination levels.
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Choose MSC or ASC certified seafood.</strong> These certifications indicate that
          the fishery or farm meets rigorous environmental standards. While the certifications do
          not directly measure microplastics, the practices they require tend to correlate with
          lower contamination.
        </li>
        <li>
          <strong>Buy local when possible.</strong> Seafood from local, well-managed fisheries
          often has shorter supply chains, meaning less time for additional contamination through
          plastic packaging and processing. Local fishmongers can also tell you exactly where and
          how the fish was caught.
        </li>
        <li>
          <strong>Prefer smaller, shorter-lived species.</strong> Smaller fish lower on the food
          chain have had less time and fewer trophic levels to accumulate microplastics. Sardines,
          mackerel, and herring (when filleted) are nutritionally excellent and tend to have lower
          contaminant loads than large, long-lived predators like swordfish or shark.
        </li>
        <li>
          <strong>Consider the source waters.</strong> Alaskan wild salmon, Norwegian cod, and
          Icelandic haddock come from some of the cleanest fishing waters on Earth. These regions
          have strong environmental regulations and relatively low coastal populations, resulting
          in lower microplastic contamination.
        </li>
        <li>
          <strong>Support companies reducing plastic in fishing gear.</strong> Ghost fishing nets
          and discarded gear are a major source of ocean microplastics. Companies and fisheries
          that actively collect and recycle old nets and gear are helping reduce the very pollution
          that contaminates seafood.
        </li>
      </ol>

      {/* How Much Seafood Is Safe */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Much Seafood Is Safe to Eat?
      </h2>
      <p>
        Despite the microplastic concern, most health organizations continue to recommend eating
        seafood two to three times per week due to its substantial nutritional benefits. The omega-3
        fatty acids, high-quality protein, vitamin D, selenium, and other nutrients in fish provide
        health benefits that are well-documented and significant. The question is not whether to eat
        seafood, but how to eat it more strategically.
      </p>
      <p>
        Based on current research, here is a practical framework for minimizing microplastic exposure
        while maintaining a healthy seafood intake:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Aim for two to three servings of filleted finfish per week.</strong> Focus on
          species like salmon, cod, trout, pollock, and halibut. These provide the nutritional
          benefits of seafood with lower microplastic exposure per serving compared to shellfish.
        </li>
        <li>
          <strong>Limit shellfish to occasional consumption.</strong> Rather than eating mussels,
          oysters, or shrimp multiple times per week, treat them as an occasional indulgence.
          Once or twice a month is a reasonable frequency that lets you enjoy these foods without
          making them a primary protein source.
        </li>
        <li>
          <strong>Vary your seafood selections.</strong> Eating a wide variety of fish from
          different sources helps ensure that you are not repeatedly exposed to the specific
          contaminants that may be concentrated in one species or region. Diversity in your
          seafood choices is a simple and effective risk-reduction strategy.
        </li>
        <li>
          <strong>Balance seafood with other protein sources.</strong> Complement your seafood
          intake with plant-based proteins, pasture-raised poultry, and other minimally processed
          protein sources to reduce your overall reliance on any single category.
        </li>
        <li>
          <strong>Pay extra attention for vulnerable populations.</strong> Pregnant women, nursing
          mothers, and young children should be particularly strategic about seafood choices. Favor
          low-mercury, low-microplastic options like wild Alaskan salmon and well-sourced cod. The
          omega-3 benefits are especially important during pregnancy and early development, but so
          is minimizing contaminant exposure.
        </li>
      </ol>

      {/* Using the MicroPlastics App */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Using the MicroPlastics App for Seafood Products
      </h2>
      <p>
        Making informed seafood choices at the grocery store or fish market can feel overwhelming
        when you are trying to balance nutritional benefits, sustainability, sourcing, and
        microplastic contamination. The MicroPlastics app simplifies this process by giving you
        instant, science-backed assessments of seafood products right at the point of purchase.
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Scan packaged seafood products.</strong> Use the app to scan frozen fish fillets,
          canned tuna, smoked salmon, and other packaged seafood items. The app evaluates the
          product based on species, sourcing, processing methods, and packaging materials to give
          you a comprehensive microplastic risk score.
        </li>
        <li>
          <strong>Compare different brands and sources.</strong> Not all salmon fillets are equal.
          The app lets you quickly compare options side by side, helping you choose the product with
          the lowest estimated microplastic risk from the options available at your store.
        </li>
        <li>
          <strong>Track your seafood consumption over time.</strong> The app&apos;s tracking
          features help you monitor how much and what types of seafood you are eating, giving you
          insight into your overall exposure patterns and helping you maintain a balanced,
          lower-risk diet.
        </li>
        <li>
          <strong>Stay updated on new research.</strong> The science of microplastics in seafood
          is rapidly evolving. The app incorporates the latest peer-reviewed findings into its
          assessments, so your decisions are always based on current evidence rather than
          outdated information.
        </li>
      </ol>

      {/* Conclusion */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Conclusion: Eat Smarter, Not Less
      </h2>
      <p>
        Microplastics in seafood are a real and growing concern, but they should not cause you to
        abandon one of the most nutritious food groups available. The health benefits of eating
        well-sourced seafood, including reduced cardiovascular risk, improved brain function, and
        essential nutrients that are difficult to obtain elsewhere, remain substantial and
        well-supported by decades of research.
      </p>
      <p>
        The key is to be strategic. Choose filleted finfish over whole shellfish as your primary
        seafood. Source from clean, well-managed fisheries in northern or remote waters. Properly
        gut, clean, and prepare your fish to remove the organs where microplastics concentrate.
        Limit high-risk items like mussels, oysters, and whole shrimp to occasional treats rather
        than dietary staples. And use tools like the MicroPlastics app to make informed decisions
        at the point of purchase.
      </p>
      <p>
        The ocean plastic crisis demands systemic solutions, including better waste management,
        reduced plastic production, and international cooperation. But while we work toward those
        long-term goals, individual choices about what seafood we buy and how we prepare it can
        meaningfully reduce our personal and our families&apos; microplastic exposure today.
      </p>
      <p className="font-semibold text-[#1E6B47]">
        Download the MicroPlastics app today to start scanning your seafood products and making
        smarter choices for your health and the health of your family.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Which seafood has the most microplastics?',
            answer:
              'Filter-feeding shellfish (mussels, oysters, clams, and other bivalves) concentrate the most microplastics because they filter large volumes of water and you eat the entire animal including the digestive tract. Small whole fish (sardines, anchovies eaten whole) are second. Top-predator fish accumulate less particle-count but more BPA and PFAS from food-chain bioaccumulation.',
          },
          {
            question: 'Which seafood is safest for microplastics?',
            answer:
              'Gut-removed fin fish from cleaner waters: wild Alaskan salmon, Pacific cod, sardines (boneless fillets, not whole), and pollock. Removing skin, gut, and dark muscle further reduces exposure. The specific water body matters more than the species in most cases.',
          },
          {
            question: 'Is wild salmon better than farmed for microplastics?',
            answer:
              'On average yes. Farmed salmon are fed pellets that contain plastic fragments and microfibers from packaging. Wild salmon accumulate plastic from their environment but typically less than farmed. Wild also tends to be lower in PCBs and dioxins.',
          },
          {
            question: 'Does cooking remove microplastics from seafood?',
            answer:
              'No. Heat does not break down microplastic particles meaningfully at cooking temperatures. The particles remain in the tissue. Cooking can drive some plasticisers (BPA, phthalates) out of the food if you cook on plastic-coated cookware, but it does not eliminate the particles already inside the fish.',
          },
          {
            question: 'Should I stop eating seafood because of microplastics?',
            answer:
              'No. Seafood remains nutritionally valuable and the microplastic exposure from a few servings per week is not catastrophic compared to the omega-3 and protein benefits. Optimize by choosing gut-removed fin fish from cleaner waters, eating less filter-feeding shellfish, and trimming skin and dark muscle.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Wootton N, et al.',
            year: 2026,
            title: 'Microplastic Impacts on Seafood: A Global Synthesis of Experimental Findings',
            publisher: 'Fish and Fisheries',
            url: 'https://onlinelibrary.wiley.com/doi/10.1111/faf.70071',
          },
          {
            authors: 'University of Amsterdam research group',
            year: 2026,
            title: 'Microplastic intake across 13 food categories in the human diet',
            publisher: 'Journal of Hazardous Materials',
            url: 'https://www.sciencedirect.com/science/article/pii/S2772502225006602',
          },
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the Release of Microplastics and Nanoplastics from Plastic Containers and Reusable Food Pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Cox KD, Covernton GA, Davies HL, et al.',
            year: 2019,
            title: 'Human Consumption of Microplastics',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b01517',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/4501',
          },
          {
            authors: 'Karami A, Golieskardi A, Choo CK, et al.',
            year: 2017,
            title: 'The presence of microplastics in commercial salts from different countries',
            publisher: 'Scientific Reports',
            url: 'https://www.nature.com/articles/srep46173',
          },
          {
            authors: 'WHO',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles',
            publisher: 'World Health Organization',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
